import { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Button,
  Input,
} from "reactstrap";
import { FormattedMessage } from "react-intl";
import { useMutation, useQueryClient } from "react-query";
import { UpdateSupervisor, PostSupervisorCompany } from "../../actions/teacherMutations";
import { GetSupervisorCompanies } from "../../actions/teacherQueries";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export default function EditSupervisorsModal({ isOpen, toggle, supervisors }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [companyId, setCompanyId] = useState(0);
  const [addNewCompany, setAddNewCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState("");

  const updateSupervisorMutation = useMutation(UpdateSupervisor);
  const postCompanyMutation = useMutation(PostSupervisorCompany);
  const companies = GetSupervisorCompanies();
  const queryClient = useQueryClient();

  const startEditing = (sv) => {
    setEditingId(sv.id);
    setName(sv.name);
    setEmail(sv.email);
    setPhone(sv.phone || "");
    setCompanyId(sv.aplCompany?.id || 0);
    setAddNewCompany(false);
    setNewCompanyName("");
  };

  const cancelEditing = () => {
    setEditingId(null);
    setAddNewCompany(false);
    setNewCompanyName("");
  };

  const hasChanged = (sv) => {
    return (
      name !== sv.name ||
      email !== sv.email ||
      phone !== (sv.phone || "") ||
      companyId !== (sv.aplCompany?.id || 0)
    );
  };

  const handleSave = (sv) => {
    if (!name.trim()) {
      toast.warn("Namn är obligatoriskt");
      return;
    }
    if (!email.trim()) {
      toast.warn("Epost är obligatoriskt");
      return;
    }
    if (!companyId || companyId <= 0) {
      toast.warn("Handledaren måste ha ett företag");
      return;
    }

    updateSupervisorMutation.mutate(
      {
        Id: sv.id,
        Name: name,
        Email: email,
        Phone: phone,
        CompanyId: companyId,
      },
      {
        onSuccess: (data) => {
          if (data && data.status >= 200 && data.status < 300) {
            toast.success("Handledare uppdaterad");
            queryClient.invalidateQueries("FetchSupervisors");
            setEditingId(null);
          } else {
            toast.warn(data);
          }
        },
        onError: (err) => {
          toast.error(err.response ? err.response.data : err.message);
        },
      }
    );
  };

  const handleCompanyChange = (value) => {
    if (value === "Add") {
      setAddNewCompany(true);
      setNewCompanyName("");
    } else {
      setCompanyId(Number(value));
    }
  };

  const handleSubmitNewCompany = () => {
    const cleanedCompanyName = newCompanyName.replace("&", "%26");
    postCompanyMutation.mutate(cleanedCompanyName, {
      onSuccess: (data) => {
        if (data && data.status >= 200 && data.status < 300) {
          toast.success("Företag tillagt");
          queryClient.invalidateQueries("FetchSupervisorCompanies");
          setCompanyId(data.data.result.id);
        } else {
          toast.warn(data);
        }
        setAddNewCompany(false);
        setNewCompanyName("");
      },
      onError: (err) => {
        toast.warn(err.response ? err.response.data : err.message);
      },
    });
  };

  const handleClose = () => {
    cancelEditing();
    toggle();
  };

  const displayEmail = (supervisorEmail) => {
    return supervisorEmail?.includes("_apl_spv")
      ? supervisorEmail.replace("_apl_spv", "")
      : supervisorEmail;
  };

  const lowerSearch = searchTerm.toLowerCase();
  const filtered = [...(supervisors || [])]
    .filter((sv) =>
      !searchTerm ||
      sv.name.toLowerCase().includes(lowerSearch) ||
      sv.email.toLowerCase().includes(lowerSearch) ||
      (sv.aplCompany?.name || "").toLowerCase().includes(lowerSearch)
    )
    .sort((a, b) => a.name.localeCompare(b.name, "sv"));

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="xl">
      <ModalHeader toggle={handleClose}>
        <FormattedMessage id="EditSupervisors" />
      </ModalHeader>
      <ModalBody>
        <Input
          type="text"
          placeholder="Sök på namn, epost eller företag..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="mb-3"
        />
        <Table responsive size="sm" className="mb-0" borderless hover>
          <thead className="bg-light">
            <tr>
              <th className="ps-3 py-2 text-muted">
                <FormattedMessage id="Name" />
              </th>
              <th className="py-2 text-muted">
                <FormattedMessage id="Email" />
              </th>
              <th className="py-2 text-muted" style={{ width: "130px" }}>
                <FormattedMessage id="Phone" />
              </th>
              <th className="py-2 text-muted">
                <FormattedMessage id="Company" />
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((sv) =>
              editingId === sv.id ? (
                <tr key={sv.id}>
                  <td className="ps-3 align-middle">
                    <Input
                      bsSize="sm"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </td>
                  <td className="align-middle">
                    <Input
                      bsSize="sm"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </td>
                  <td className="align-middle">
                    <Input
                      bsSize="sm"
                      type="text"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </td>
                  <td className="align-middle">
                    {addNewCompany ? (
                      <div className="d-flex align-items-center">
                        <Input
                          bsSize="sm"
                          type="text"
                          value={newCompanyName}
                          onChange={(e) => setNewCompanyName(e.target.value)}
                          placeholder="Företagsnamn"
                        />
                        <Button
                          size="sm"
                          color="primary"
                          outline
                          className="ms-1"
                          onClick={handleSubmitNewCompany}
                          disabled={!newCompanyName.trim()}
                        >
                          <FontAwesomeIcon icon="check" />
                        </Button>
                        <Button
                          size="sm"
                          color="secondary"
                          outline
                          className="ms-1"
                          onClick={() => setAddNewCompany(false)}
                        >
                          <FontAwesomeIcon icon="times" />
                        </Button>
                      </div>
                    ) : (
                      <Input
                        bsSize="sm"
                        type="select"
                        value={companyId}
                        onChange={(e) => handleCompanyChange(e.target.value)}
                      >
                        <option value="0">Välj företag</option>
                        <option value="Add">Lägg till nytt företag</option>
                        {companies.data?.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </Input>
                    )}
                  </td>
                  <td className="align-middle text-end text-nowrap">
                    {hasChanged(sv) && (
                      <Button
                        color="primary"
                        outline
                        size="sm"
                        className="me-1"
                        disabled={updateSupervisorMutation.isLoading}
                        onClick={() => handleSave(sv)}
                      >
                        <FontAwesomeIcon icon="save" />
                      </Button>
                    )}
                    <Button
                      color="secondary"
                      outline
                      size="sm"
                      onClick={cancelEditing}
                    >
                      <FontAwesomeIcon icon="times" />
                    </Button>
                  </td>
                </tr>
              ) : (
                <tr
                  key={sv.id}
                  style={{ cursor: "pointer" }}
                  onClick={() => startEditing(sv)}
                >
                  <td className="ps-3 align-middle">{sv.name}</td>
                  <td className="align-middle">{displayEmail(sv.email)}</td>
                  <td className="align-middle">{sv.phone}</td>
                  <td className="align-middle">{sv.aplCompany?.name}</td>
                </tr>
              )
            )}
          </tbody>
        </Table>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={handleClose}>
          <FormattedMessage id="Close" />
        </Button>
      </ModalFooter>
    </Modal>
  );
}
